import {
  LegalAccountTaxationType,
  type SecurityGroupPosition,
  type SecurityPosition,
  type SecurityPositionBySecurity,
  isInstitutionalSecurityPosition,
  isInstitutionalSecurityPositionForSubAccount,
} from './data';
import { SecurityPositionItem, SecurityPositionItemWithMultipleSubAccounts } from './SecurityPositionItem';

import type { SellButtonComponent } from './SecurityGroupPositionSheet';

const is401kTaxationType = (taxationType: LegalAccountTaxationType | undefined): boolean => {
  return (
    taxationType === LegalAccountTaxationType.Traditional_401K || taxationType === LegalAccountTaxationType.Roth_401K
  );
};

function areAllSubAccounts401ks(securityPosition: SecurityPositionBySecurity): boolean {
  return securityPosition.securityPositionsBySubAccount.every(sp =>
    is401kTaxationType(sp.subAccount?.legalAccount?.taxationType)
  );
}

function isSubAccountA401k(securityGroupPosition: SecurityGroupPosition): boolean {
  const legalAccount =
    securityGroupPosition.securityPositionsBySecurity[0]?.securityPositionsBySubAccount[0]?.subAccount.legalAccount;
  return legalAccount ? is401kTaxationType(legalAccount.taxationType) : false;
}

function financialSecurityIdFromSecurityPositionBySecurity(
  securityPositionBySecurity: SecurityPositionBySecurity
): string {
  const firstPosition = securityPositionBySecurity.securityPositionsBySubAccount[0]?.securityPositions[0];
  if (firstPosition && isInstitutionalSecurityPositionForSubAccount(firstPosition)) {
    return firstPosition.financialSecurity.id;
  }

  return '';
}

function financialSecurityIdFromSecurityPosition(securityPosition: SecurityPosition): string {
  if (isInstitutionalSecurityPosition(securityPosition)) {
    return securityPosition.financialSecurity.id;
  }

  return '';
}

export function SecurityPositionItems({
  securityGroupPosition,
  sellButton: SellButton,
}: {
  securityGroupPosition: SecurityGroupPosition;
  sellButton?: SellButtonComponent;
}) {
  const hasMultipleSubAccounts = securityGroupPosition.securityPositionsBySecurity.some(
    sp => sp.securityPositionsBySubAccount.length > 1
  );

  if (hasMultipleSubAccounts) {
    return securityGroupPosition.securityPositionsBySecurity.map(securityPositionBySecurity => {
      const security = securityPositionBySecurity.securityPositionsBySubAccount[0]?.securityPositions[0]?.security;

      if (!security) return null;

      return (
        <SecurityPositionItemWithMultipleSubAccounts
          key={securityPositionBySecurity.id}
          securityPositionBySecurity={securityPositionBySecurity}
          sellButton={
            SellButton && (
              <SellButton
                financialSecurityId={financialSecurityIdFromSecurityPositionBySecurity(securityPositionBySecurity)}
                assetTicker={security.symbol}
                shouldShow401kBlocked={areAllSubAccounts401ks(securityPositionBySecurity)}
              />
            )
          }
        />
      );
    });
  }

  return securityGroupPosition.securityPositions.map(securityPosition => (
    <SecurityPositionItem
      key={securityPosition.security.id}
      securityPosition={securityPosition}
      sellButton={
        SellButton && (
          <SellButton
            financialSecurityId={financialSecurityIdFromSecurityPosition(securityPosition)}
            assetTicker={securityPosition.security.symbol}
            shouldShow401kBlocked={isSubAccountA401k(securityGroupPosition)}
          />
        )
      }
    />
  ));
}
